import useDeleteLaundryMutation from "api/mutations/laundry/useDeleteLaundry"
import useGetLaundries from "api/querries/laundry/useGetLaundries"
import { useAppContext } from "providers/AppProvider/AppProvider"
import { useUI } from "providers/UIProvider"
import { UI_INITIAL_STATE } from "providers/UIProvider/UIProviderConstants"
import { createContext, useContext, useEffect, useMemo, useReducer, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"

import { LAUNDRIES_INITIAL_CONTEXT, LAUNDRIES_INITIAL_STATE } from "./LaundriesProviderConstants"

import { ILaundiesProviderProps, ILaundriesContext } from "./LaundriesProviderTypes"
import useDebounce from "hooks/useDebounce"
import { IGetLaundriesQueryParams } from "types/laundry"
import { IRoles } from "types/shared"

export const LaundriesContext = createContext<ILaundriesContext>(LAUNDRIES_INITIAL_CONTEXT)

export const useLaundriesContext: () => ILaundriesContext = () => useContext(LaundriesContext) 


const LaundriesProvider: React.FC<ILaundiesProviderProps> = ({ children }) => {
  const navigate = useNavigate()
  const { search } = useLocation()
  const { userRole } = useAppContext()
  const { toggleModal } = useUI()
  
  const searchParams = new URLSearchParams(search)

  const isQappUser = userRole && [IRoles.QAPP_ADMIN, IRoles.QAPP_MANAGER].includes(userRole);

  const [state, setState] = useReducer(
    (oldState: any, newState: any) => ({ ...oldState, ...newState }),
    {
      ...LAUNDRIES_INITIAL_STATE,
      searchValue: searchParams.get("search") || "",
      isOpenedChecked: Boolean(searchParams.get("isOpen") === "true"),
      isClosedChecked: Boolean(searchParams.get("isOpen") === "false"),
    },
  )

  const debouncedSearchValue = useDebounce(state.searchValue, 1000)

  const [query, setQuery] = useState<IGetLaundriesQueryParams>({
    search: searchParams.get("search") || "",
    sortBy: searchParams.get("sortBy") || "id",
    isOpen: (searchParams.get("isOpen") === "null" || searchParams.get("isOpen") === null) ? null : Boolean(searchParams.get("isOpen") === "true"),
    pageIndex: Number(searchParams.get("pageIndex")) || 1,
    pageSize: Number(searchParams.get("pageSize")) || 10
  })

  const { data: laundriesData, isFetched: laundriesDataFetched, refetch: refetchLaundriesData, error: laundriesDataError } = useGetLaundries({
    query,
    enabled: isQappUser || false
  })
  const { mutate: deleteLaundry, isSuccess: deleteLaundrySuccess, isLoading: deleteLaundryLoading }  = useDeleteLaundryMutation()


  const updateSearchParams = (query: any) => {
    navigate({
      search: `?${new URLSearchParams({
        ...query
      }).toString()}`
    })
  }

  const handleLaundriesPageChange = (page: number) => {
    const updatedQuery = {
      ...query,
      pageIndex: page
    }
    updateSearchParams(updatedQuery)
    setQuery(updatedQuery)
  }

  const handleSearchChange = (e: any) => setState({ searchValue: e.target.value })

  const handleStatusCheckboxClick = (keyName: "isOpenedChecked" | "isClosedChecked") => {
    if(keyName === "isOpenedChecked") {
      const newOpened = !state.isOpenedChecked
      const newClsoed = newOpened ? false : state.isClosedChecked
      setState({ isOpenedChecked: newOpened, isClosedChecked: newClsoed })
    }

    if(keyName === "isClosedChecked") {
      const newClsoed = !state.isClosedChecked
      const newOpened = newClsoed ? false : state.isOpenedChecked
      setState({ isOpenedChecked: newOpened, isClosedChecked: newClsoed })
    }
  }

  useEffect(()=>{
    if(laundriesData?.status === 400) {
      navigate("/")
    }

    if(laundriesDataFetched) {
      setState({ laundries: laundriesData.data })
    }
  },[laundriesData])

  useEffect(()=>{
    if(query && isQappUser) {
      refetchLaundriesData()
    }
}, [query])

useEffect(()=>{
    const updatedQuery = {
      ...query,
      search: debouncedSearchValue,
      pageIndex: 1
    }
    updateSearchParams(updatedQuery)
    setQuery(updatedQuery)
},[debouncedSearchValue])

useEffect(()=>{
  if(deleteLaundrySuccess) {
    toggleModal("confirmationModal", false, UI_INITIAL_STATE.modals.confirmationModal.data)
    refetchLaundriesData()
  }
},[deleteLaundrySuccess])

useEffect(()=>{
  let updatedQuery = {...query}
  
  if(state.isOpenedChecked && !state.isClosedChecked) { 
    updatedQuery = {
      ...updatedQuery,
      isOpen: true,
      pageIndex: 1
    }
  } else if(!state.isOpenedChecked && state.isClosedChecked) {
    updatedQuery = {
      ...updatedQuery,
      isOpen: false,
      pageIndex: 1
    }
  } else {
    updatedQuery = {
      ...updatedQuery,
      isOpen: null,
      pageIndex: 1
    }
  }
   
    updateSearchParams(updatedQuery)
    setQuery(updatedQuery)  
},[state.isOpenedChecked, state.isClosedChecked])

  const value = useMemo(()=>({
    ...state,
    query,
    handleLaundriesPageChange,
    deleteLaundry,
    handleSearchChange,
    handleStatusCheckboxClick
  }),[state])

  return <LaundriesContext.Provider value={value}>
    {children}
  </LaundriesContext.Provider>
}

export default LaundriesProvider